/**
 * Greenfield Service
 *
 * This service provides dedicated methods for interacting with FHIR APIs.
 * Supports multiple FHIR sources:
 * - Meditech Greenfield (sandbox/development)
 * - HCA TriStar (production - future)
 */

import axios from 'axios';
import { normalizePatientData, normalizeSummary } from '../adapters/patientDataAdapter';
import {
  mapFhirPatientToVirtualPAT,
  getLocalSurgeryInfo,
  createSurgeryInfoForPatient,
} from './greenfieldEnhancedMapper';
import { getFhirSource, getFhirSourceHeaders } from '../config/fhirSources';

// Base URL for Greenfield API
const GREENFIELD_API_BASE_URL = '/api/greenfield';
const FHIR_API_BASE_URL = '/api/fhir';

/**
 * Get headers for FHIR API requests
 * Includes VirtualPAT session token for authentication with the worker proxy
 * and X-FHIR-Source header for multi-source routing
 * @param {string} sourceOverride - Optional source override
 * @returns {Object} Headers object with Authorization, Accept, and source headers
 */
export function getGreenfieldHeaders(sourceOverride) {
  // Get access token from localStorage - check all possible keys
  const accessToken =
    localStorage.getItem('_at') ||
    localStorage.getItem('token') ||
    localStorage.getItem('authToken');

  return {
    Accept: 'application/fhir+json',
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    ...getFhirSourceHeaders(sourceOverride),
  };
}

/**
 * Handle rate limiting errors
 * @param {Error} error - The error object
 * @returns {Promise<void>} A promise that resolves after waiting
 * @private
 */
async function _handleRateLimitError(error) {
  if (error.response && error.response.status === 429) {
    // Get retry-after header if available
    const retryAfter = error.response.headers['retry-after'];
    const waitTime = retryAfter ? parseInt(retryAfter, 10) * 1000 : 60000; // Default to 60 seconds

    console.warn(
      `[GreenfieldService] Rate limit exceeded. Waiting ${waitTime / 1000} seconds before retrying.`
    );

    // Wait for the specified time
    return new Promise(resolve => setTimeout(resolve, waitTime));
  }

  // Not a rate limit error, just rethrow
  throw error;
}

/**
 * Retry a function with exponential backoff
 * @param {Function} fn - The function to retry
 * @param {number} maxRetries - Maximum number of retries
 * @returns {Promise<any>} The result of the function
 * @private
 */
async function _retryWithBackoff(fn, maxRetries = 3) {
  let lastError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // Check if it's a 401 Unauthorized error
      if (error.response && error.response.status === 401) {
        console.error('[GreenfieldService] Unauthorized access');
        // Do not retry on 401 (VirtualPAT auth is required for worker proxied requests)
        throw error;
      }

      // Check if it's a rate limit error
      if (error.response && error.response.status === 429) {
        // Get retry-after header if available
        const retryAfter = error.response.headers['retry-after'];
        const baseWaitTime = retryAfter ? parseInt(retryAfter, 10) * 1000 : 60000; // Default to 60 seconds

        // Calculate exponential backoff
        const waitTime = baseWaitTime * 2 ** attempt;

        console.warn(
          `[GreenfieldService] Rate limit exceeded. Retry ${attempt + 1}/${maxRetries}. Waiting ${waitTime / 1000} seconds.`
        );

        // Wait for the calculated time
        await new Promise(resolve => setTimeout(resolve, waitTime));
      } else {
        // Not a rate limit error, just throw
        throw error;
      }
    }
  }

  // If we've exhausted all retries
  console.error(`[GreenfieldService] Max retries (${maxRetries}) exceeded.`);
  throw lastError;
}

/**
 * Fetch all patients from Greenfield
 * @param {Object} options - Optional parameters
 * @param {string} options.patientId - If provided, fetch only this patient
 * @returns {Promise<Array>} Array of normalized patient data
 */
export async function getGreenfieldPatients(options = {}) {
  return _retryWithBackoff(async () => {
    try {
      let url = `${GREENFIELD_API_BASE_URL}/patient`;

      // If specific patient ID is requested, fetch just that patient
      if (options.patientId) {
        url = `${GREENFIELD_API_BASE_URL}/patient/${options.patientId}`;
      }

      const response = await axios.get(url, {
        headers: getGreenfieldHeaders(),
      });

      console.log('[GreenfieldService] Raw patient data:', response.data);

      // Handle single patient response vs bundle
      let entries = [];
      if (options.patientId && response.data.resourceType === 'Patient') {
        // Single patient response
        entries = [{ resource: response.data }];
      } else if (response.data.entry) {
        // Bundle response
        entries = Array.isArray(response.data.entry) ? response.data.entry : [];
      }

      // Map and normalize patients with enhanced mapping
      const normalizedPatients = entries.map(entry => {
        // First try the enhanced mapper that looks for surgery info
        const enhancedPatient = mapFhirPatientToVirtualPAT(entry.resource);

        // Check if we have local surgery info stored
        const localSurgeryInfo = getLocalSurgeryInfo(entry.resource.id);
        if (localSurgeryInfo) {
          enhancedPatient.surgeryType = localSurgeryInfo.surgeryType;
          enhancedPatient.surgeryDate = localSurgeryInfo.surgeryDate;
          enhancedPatient.surgeonId = localSurgeryInfo.surgeonId;
          enhancedPatient.surgeonName = localSurgeryInfo.surgeonName;
          enhancedPatient.asaScore = localSurgeryInfo.asaScore;
          enhancedPatient.needsSurgeryInfo = false;
        }

        // Also normalize using the standard adapter for consistency
        const normalizedPatient = normalizePatientData(enhancedPatient, 'greenfield');
        normalizedPatient._source = 'greenfield'; // Explicitly mark as Greenfield source
        normalizedPatient.needsSurgeryInfo = enhancedPatient.needsSurgeryInfo;

        return normalizedPatient;
      });

      console.log('[GreenfieldService] Normalized patients:', normalizedPatients);
      return normalizedPatients;
    } catch (error) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        'Unknown error fetching Greenfield patients';
      console.error('[GreenfieldService] Error fetching patients:', errorMsg, error);

      // Check if it's a rate limit error
      if (error.response && error.response.status === 429) {
        // Let the retry mechanism handle it
        throw error;
      }

      throw new Error(`Failed to fetch Greenfield patients: ${errorMsg}`);
    }
  });
}

/**
 * Fetch a single patient by ID from Greenfield
 * @param {string} patientId - The ID of the patient to fetch
 * @returns {Promise<Object>} Normalized patient data
 */
export async function getGreenfieldPatient(patientId) {
  return _retryWithBackoff(async () => {
    try {
      const response = await axios.get(
        `${GREENFIELD_API_BASE_URL}/patient/${patientId}?include=true`,
        {
          headers: getGreenfieldHeaders(),
        }
      );

      console.log('[GreenfieldService] Raw patient detail:', response.data);

      // Normalize the patient data
      const normalizedPatient = normalizePatientData(
        response.data.patient || response.data,
        'greenfield'
      );
      normalizedPatient._source = 'greenfield'; // Explicitly mark as Greenfield source

      // Add related resources if available
      if (response.data.related) {
        normalizedPatient.conditions = response.data.related.Condition;
        normalizedPatient.medicationsRaw = response.data.related.MedicationRequest;
        normalizedPatient.procedures = response.data.related.Procedure;
        normalizedPatient.observationsRaw = response.data.related.Observation;
      }

      return normalizedPatient;
    } catch (error) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        'Unknown error fetching Greenfield patient';
      console.error('[GreenfieldService] Error fetching patient by ID:', errorMsg, error);

      // Check if it's a rate limit error
      if (error.response && error.response.status === 429) {
        // Let the retry mechanism handle it
        throw error;
      }

      throw new Error(`Failed to fetch Greenfield patient: ${errorMsg}`);
    }
  });
}

/**
 * Fetch a patient's preop summary from Greenfield
 * @param {string} patientId - The ID of the patient
 * @returns {Promise<Object>} Normalized preop summary data
 */
export async function getGreenfieldPatientSummary(patientId) {
  return _retryWithBackoff(async () => {
    try {
      // First get the patient with all related resources
      const patient = await getGreenfieldPatient(patientId);

      // Create a summary object from the patient data
      const summary = {
        patient,
        allergies: patient.allergies || [],
        medications: patient.medications || [],
        conditions: patient.conditions || [],
        observations: patient.observationsRaw || [],
        procedures: patient.procedures || [],
      };

      // Normalize the summary
      return normalizeSummary(summary, 'greenfield');
    } catch (error) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        'Unknown error fetching Greenfield patient summary';
      console.error('[GreenfieldService] Error fetching patient summary:', errorMsg, error);

      // Check if it's a rate limit error
      if (error.response && error.response.status === 429) {
        // Let the retry mechanism handle it
        throw error;
      }

      throw new Error(`Failed to fetch Greenfield patient summary: ${errorMsg}`);
    }
  });
}

/**
 * Validate the current token with strict checking (no fallbacks)
 * @returns {Promise<Object>} Validation result with authentication status
 */
export async function validateGreenfieldToken() {
  try {
    const response = await axios.get(`${GREENFIELD_API_BASE_URL}/validate-token`, {
      headers: getGreenfieldHeaders(),
    });

    return {
      success: true,
      authenticated: response.data.authenticated,
      message: response.data.message,
      data: response.data.data,
    };
  } catch (error) {
    const errorMsg =
      error?.response?.data?.message || error?.message || 'Unknown error during token validation';
    console.error('[GreenfieldService] Token validation failed:', errorMsg, error);

    // Extract the authentication status from the error response if available
    const authenticated = error.response?.data?.authenticated === true;

    return {
      success: false,
      authenticated,
      message: errorMsg || `Token validation failed: ${error.message}`,
      error,
    };
  }
}

/**
 * Fetch worker-side Greenfield integration status (no token returned to browser).
 * @returns {Promise<Object>} Status payload from the worker
 */
export async function getGreenfieldStatus() {
  const response = await axios.get(`${GREENFIELD_API_BASE_URL}/status`, {
    headers: getGreenfieldHeaders(),
  });
  return response.data?.status || response.data;
}

/**
 * Test the Greenfield connection with the current token
 * @param {boolean} strictValidation - If true, uses strict validation without fallbacks
 * @returns {Promise<Object>} Test result with success status and message
 */
export async function testGreenfieldConnection(strictValidation = false) {
  if (strictValidation) {
    // Use the strict validation endpoint
    return validateGreenfieldToken();
  }

  // Use the regular metadata endpoint with fallbacks
  return _retryWithBackoff(async () => {
    try {
      const response = await axios.get(`${GREENFIELD_API_BASE_URL}/metadata`, {
        headers: getGreenfieldHeaders(),
      });

      return {
        success: true,
        message: 'Successfully connected to Greenfield API',
        data: response.data,
      };
    } catch (error) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        'Unknown error testing Greenfield connection';
      console.error('[GreenfieldService] Connection test failed:', errorMsg, error);

      // Check if it's a rate limit error
      if (error.response && error.response.status === 429) {
        // Let the retry mechanism handle it
        throw error;
      }

      return {
        success: false,
        message: `Failed to connect to Greenfield API: ${errorMsg}`,
        error,
      };
    }
  });
}

/**
 * Get rate limiter status from the backend
 * @returns {Promise<Object>} Rate limiter status
 */
export async function getGreenfieldRateLimiterStatus() {
  try {
    const response = await axios.get(`${GREENFIELD_API_BASE_URL}/rate-limiter-status`);
    return response.data;
  } catch (error) {
    console.error('[GreenfieldService] Error fetching rate limiter status:', error);
    return {
      error: error.message,
      status: {
        default: { circuitOpen: false, queueLength: 0 },
        token: { circuitOpen: false, queueLength: 0 },
      },
    };
  }
}

/**
 * Save surgery information for a Greenfield patient
 * @param {string} patientId - The patient ID
 * @param {Object} surgeryInfo - Surgery information to save
 * @returns {Object} The saved surgery data
 */
export function saveGreenfieldPatientSurgeryInfo(patientId, surgeryInfo) {
  return createSurgeryInfoForPatient(patientId, surgeryInfo);
}

/**
 * Get all FHIR sources status (multi-source endpoint)
 * @returns {Promise<Object>} Sources status with connectivity info
 */
export async function getFhirSourcesStatus() {
  try {
    const response = await axios.get(`${FHIR_API_BASE_URL}/sources`, {
      headers: getGreenfieldHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error('[GreenfieldService] Error fetching FHIR sources status:', error);
    return {
      success: false,
      error: error.message,
      activeSource: 'greenfield',
      sources: {},
    };
  }
}

// Re-export source selection functions for convenience
export { getFhirSource, setFhirSource, getFhirSourceConfig, getAllFhirSources } from '../config/fhirSources';

export default {
  getGreenfieldHeaders,
  getGreenfieldPatients,
  getGreenfieldPatient,
  getGreenfieldPatientSummary,
  testGreenfieldConnection,
  validateGreenfieldToken,
  getGreenfieldStatus,
  saveGreenfieldPatientSurgeryInfo,
  getFhirSourcesStatus,
};
